import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { menuItems, ViewType, MenuItem } from './menuConfig';
import { cn } from '../ui/utils';
import { InBooks24Logo } from '../InBooks24Logo';
import { useAuth } from '../../contexts/AuthContext';

interface NavigationSidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export function NavigationSidebar({ isCollapsed, onToggle }: NavigationSidebarProps) {
  const location = useLocation();
  const [activeView, setActiveView] = useState<ViewType>(ViewType.dashboard);
  const { userMenuPermissions } = useAuth();

  const handleMenuClick = (viewType: ViewType) => {
    setActiveView(viewType);
  };

  // Filter menu items based on user permissions
  // Only show menus that user has at least 'view' permission for
  const filteredMenuItems = menuItems.filter(item => {
    const exactPermission = userMenuPermissions.find(p => p.route === item.path && p.isActive);
    const permission =
      exactPermission ||
      userMenuPermissions.find((p) => {
        const parentRoute = `/${item.path.split('/').filter(Boolean)[0]}`;
        return p.route === parentRoute && p.isActive;
      });
    const hasPermission = permission && permission.canView;

    // Sales dashboard should not inherit `/dashboard` permission
    if (item.path === '/dashboard/sales-dashboard') {
      return Boolean(exactPermission && exactPermission.canView);
    }
    
    // Temporarily show challan menu even without permission (for development)
    // TODO: Remove this once backend menu permission is set up
    // if (item.path === '/challans') {
    //   console.log('🔍 Challan menu item check:', {
    //     itemPath: item.path,
    //     permission,
    //     hasPermission,
    //     userMenuPermissions: userMenuPermissions.filter(p => p.route && (p.route.includes('challan') || p.route.includes('purchase')))
    //   });
    //   // Show challan menu even if no permission found (for development)
    //   return true;
    // }
    
    return hasPermission;
  });


  // Group filtered menu items by category
  const dashboardItems = filteredMenuItems.filter(item => item.category === 'dashboard');
  const masterDataItems = filteredMenuItems.filter(item => item.category === 'master');
  const salesItems = filteredMenuItems.filter(item => item.category === 'sales');
  const productionItems = filteredMenuItems.filter(item => item.category === 'production');
  const accountItems = filteredMenuItems.filter(item => item.category === 'account');
  const adminItems = filteredMenuItems.filter(item => item.category === 'admin');
  const packingItems = filteredMenuItems.filter(item => item.category === 'packing');
  const storeItems = filteredMenuItems.filter(item => item.category === 'store');
  const invoiceItems = filteredMenuItems.filter(item => item.category === 'invoice');
  const challanItems = filteredMenuItems.filter(item => item.category === 'challan');
  const reportsItems = filteredMenuItems.filter(item => item.category === 'reports');

  const renderMenuGroup = (items: MenuItem[], title?: string) => {
    // Don't render the group if there are no items
    if (items.length === 0) {
      return null;
    }

    return (
      <div className="mb-6">
        {title && !isCollapsed && (
          <div className="px-3 py-2 mb-2">
            <h4 className="text-xs font-semibold text-primary-700 uppercase tracking-wider">
              {title}
            </h4>
          </div>
        )}
        <div className="space-y-1">
          {items.map((item) => {
            const isActive =
              location.pathname === item.path ||
              (item.path === '/packing-entries/box-master' &&
                location.pathname.startsWith('/packing-entries/box-master'));
            const Icon = item.icon;
            
            const menuButton = (
              <Button
                key={item.id}
                asChild
                variant={isActive ? "default" : "ghost"}
                className={cn(
                  "w-full h-auto min-h-[2.25rem]",
                  isCollapsed ? "justify-center px-0" : "justify-start",
                  isActive 
                    ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white" 
                    : "text-secondary-600 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100/50 hover:text-primary-700"
                )}
              >
                <Link 
                  to={item.path} 
                  onClick={() => handleMenuClick(item.id)} 
                  className={cn(
                    "flex items-center w-full h-full",
                    isCollapsed ? "justify-center px-0" : "justify-start px-4"
                  )}
                >
                  <Icon className={cn(
                    "w-4 h-4 flex-shrink-0",
                    isCollapsed ? "" : "mr-3"
                  )} />
                  {!isCollapsed && <span className="truncate flex-1">{item.label}</span>}
                </Link>
              </Button>
            );

            // Wrap with tooltip only when collapsed
            if (isCollapsed) {
              return (
                <TooltipPrimitive.Root key={item.id} delayDuration={300}>
                  <TooltipTrigger asChild>
                    {menuButton}
                  </TooltipTrigger>
                  <TooltipPrimitive.Portal>
                    <TooltipPrimitive.Content
                      side="right"
                      sideOffset={8}
                      className="bg-white text-primary-600 border border-primary-500 shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit rounded-md px-3 py-1.5 text-xs text-balance"
                    >
                      {item.label}
                    </TooltipPrimitive.Content>
                  </TooltipPrimitive.Portal>
                </TooltipPrimitive.Root>
              );
            }

            return menuButton;
          })}
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider>
      <div className={cn(
        "flex flex-col h-full bg-gradient-to-b from-white to-primary-50/30 border-r border-primary-200 transition-all duration-300 overflow-hidden",
        isCollapsed ? "w-16" : "w-64"
      )}>
        {/* Header - Must align perfectly with AppHeader */}
        <div className="flex-shrink-0 flex items-center justify-center h-16 px-4 border-b border-primary-200/50 bg-gradient-to-r from-white/90 via-primary-50/30 to-white/90 backdrop-blur-sm shadow-sm min-h-[64px]">
          {!isCollapsed ? (
            <InBooks24Logo size="md" variant="horizontal" showTagline={true} showIcon={true} />
          ) : (
            <InBooks24Logo size="lg" variant="icon-only" showTagline={false} />
          )}
        </div>

        {/* Navigation Menu */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-4">
          {/* Dashboard */}
          {renderMenuGroup(dashboardItems, "DASHBOARD")}

          {/* Reports */}
          {renderMenuGroup(reportsItems, "REPORTS")}
          
          {/* Master Data */}
          {renderMenuGroup(masterDataItems, "MASTER DATA")}
          
          {/* Sales & Orders */}
          {renderMenuGroup(salesItems, "PURCHASES & SALES")}
          
          {/* Production */}
          {renderMenuGroup(productionItems, "PRODUCTION")}
          
          {/* Packing */}
          {renderMenuGroup(packingItems, "PACKING")}
          
          {/* Store Management */}
          {renderMenuGroup(storeItems, "STORE MANAGEMENT")}
          {/* Invoice Management */}
          {renderMenuGroup(invoiceItems, "INVOICE MANAGEMENT")}
          
          {/* Account */}
          {renderMenuGroup(accountItems, "ACCOUNT")}
          {/* CHALLAN */}
          {renderMenuGroup(challanItems, "CHALLAN")}
          
          {/* System Administration */}
          {renderMenuGroup(adminItems, "SYSTEM ADMIN")}
          </div>
        </ScrollArea>
      </div>
    </div>
    </TooltipProvider>
  );
}